import { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { articlesStore, localizeArticle } from "@/store/articlesStore";
import type { ManagedArticle } from "@/store/articlesStore";
import { onRealtimeEvent } from "@/lib/realtimeManager";
import { throttle } from "@/lib/throttle";

const ARTICLES_EVENT = "alsiyahiah:articles:updated";

// In-flight request deduplication
const inFlight = new Map<string, Promise<ManagedArticle[]>>();

async function deduped(key: string, fn: () => Promise<ManagedArticle[]>): Promise<ManagedArticle[]> {
  if (inFlight.has(key)) return inFlight.get(key)!;
  const p = fn().finally(() => inFlight.delete(key));
  inFlight.set(key, p);
  return p;
}

export function useArticlesStore() {
  const { i18n } = useTranslation();
  const [articles, setArticles] = useState<ManagedArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const refresh = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    const data = await deduped("all:300", () => articlesStore.getAll(300));
    if (mountedRef.current) {
      setArticles(data.map((article) => localizeArticle(article, i18n.language)));
      setLoading(false);
    }
  }, [i18n.language]);

  useEffect(() => {
    refresh(false);
    // Realtime updates: throttle لتجنب تكرار re-render المستمر
    const throttledRefresh = throttle(() => refresh(true), 2000);
    const unsub = onRealtimeEvent(ARTICLES_EVENT, throttledRefresh);
    return unsub;
  }, [refresh]);

  return { articles, refresh, loading };
}

/**
 * Hook للمقالات المنشورة فقط (للموقع العام)
 * stale-while-revalidate: يجلب من الكاش أولاً، ثم يجلب من Supabase فوراً
 */
export function usePublishedArticles(enabled = true, limit = 20) {
  const { i18n } = useTranslation();
  const [articles, setArticles] = useState<ManagedArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const mountedRef = useRef(true);
  const limitRef = useRef(limit);
  limitRef.current = limit;

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const refresh = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    const data = await deduped(`pubfeat:${limitRef.current}`, () => articlesStore.getPublishedAndFeatured(limitRef.current));
    if (mountedRef.current) {
      setArticles(data.map((article) => localizeArticle(article, i18n.language)));
      setLoading(false);
    }
  }, [i18n.language]);

  useEffect(() => {
    if (!enabled) return;
    refresh(false);
    const throttledRefresh = throttle(() => refresh(true), 2000);
    const unsub = onRealtimeEvent(ARTICLES_EVENT, throttledRefresh);
    return unsub;
  }, [enabled, refresh]);

  return { articles, loading };
}

/**
 * Hook لمقال واحد بالـ slug
 */
export function useArticleBySlug(slug: string) {
  const { i18n } = useTranslation();
  const [article, setArticle] = useState<ManagedArticle | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const mountedRef = useRef(true);
  const slugRef = useRef(slug);
  slugRef.current = slug;

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const refresh = useCallback(async (silent = false) => {
    if (!slugRef.current) { setLoading(false); return; }
    if (!silent) setLoading(true);
    const data = await articlesStore.getBySlug(slugRef.current);
    if (mountedRef.current) {
      setArticle(data ? localizeArticle(data, i18n.language) : data);
      setLoading(false);
    }
  }, [i18n.language]);

  useEffect(() => {
    refresh(false);
    const throttledRefresh = throttle(() => refresh(true), 2000);
    const unsub = onRealtimeEvent(ARTICLES_EVENT, throttledRefresh);
    return unsub;
  }, [refresh, slug]);

  return { article, loading };
}

/**
 * Hook لمقالات تصنيف معين
 */
export function useCategoryArticles(categorySlug: string) {
  const { i18n } = useTranslation();
  const [articles, setArticles] = useState<ManagedArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const slugRef = useRef(categorySlug);
  slugRef.current = categorySlug;
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const refresh = useCallback(async (silent = false) => {
    if (!slugRef.current) { setLoading(false); return; }
    if (!silent) setLoading(true);
    const data = await deduped(`cat:${slugRef.current}:100`, () =>
      articlesStore.getByCategory(slugRef.current, 100)
    );
    if (mountedRef.current) {
      setArticles(data.map((article) => localizeArticle(article, i18n.language)));
      setLoading(false);
    }
  }, [i18n.language]);

  useEffect(() => {
    refresh(false);
    const throttledRefresh = throttle(() => refresh(true), 2000);
    const unsub = onRealtimeEvent(ARTICLES_EVENT, throttledRefresh);
    return unsub;
  }, [refresh, categorySlug]);

  return { articles, loading };
}

/**
 * Hook لمقالات تصنيف معين (primary + secondary category match)
 * يستخدم لتصفية المقالات حسب التصنيف الفرعي للمدن
 */
export function useCategoryOrSecondaryArticles(categorySlug: string) {
  const { i18n } = useTranslation();
  const [articles, setArticles] = useState<ManagedArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const slugRef = useRef(categorySlug);
  slugRef.current = categorySlug;
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const refresh = useCallback(async (silent = false) => {
    if (!slugRef.current) { setLoading(false); return; }
    if (!silent) setLoading(true);
    const data = await deduped(`catsec:${slugRef.current}:100`, () =>
      articlesStore.getByCategoryOrSecondary(slugRef.current, 100)
    );
    if (mountedRef.current) {
      setArticles(data.map((article) => localizeArticle(article, i18n.language)));
      setLoading(false);
    }
  }, [i18n.language]);

  useEffect(() => {
    refresh(false);
    const throttledRefresh = throttle(() => refresh(true), 2000);
    const unsub = onRealtimeEvent(ARTICLES_EVENT, throttledRefresh);
    return unsub;
  }, [refresh, categorySlug]);

  return { articles, loading };
}
